// MessagesList.tsx
import React, { useEffect } from "react";
import { Message } from "../utils/types";
import MessageRow from "./MessageRow";
import SuggestionButtons from "./SuggestionButtons"; 
import { SuggestionPhrase } from "../utils/phrasesData";

interface MessagesListProps {
  messages: Message[];
  isStreaming: boolean;
  isTyping: boolean;
  bottomRef: React.RefObject<HTMLDivElement | null>;
  sendMessage: (msg: string, backendPayload?: string) => void;
  userAvatar: string;
  botAvatar: string;
  scrollCarousel: (index: number, dir: number) => void;
  carouselRefs: React.MutableRefObject<Record<number, HTMLDivElement | null>>;
  messagesContainerRef: React.RefObject<HTMLDivElement | null>;
  isDarkMode: boolean; 
  isChatEmpty: boolean; 
  setResponding: React.Dispatch<React.SetStateAction<boolean>>
  isResponding: boolean;
  initialLoading: boolean; 
  setInput: (val: string) => void;
  selectedMultiChips: string[];
  setSelectedMultiChips: React.Dispatch<React.SetStateAction<string[]>>;
}

const MessagesList = React.memo<MessagesListProps>(({
  messages,
  isTyping,
  bottomRef,
  sendMessage,
  isResponding,
  userAvatar,
  botAvatar,
  scrollCarousel,
  carouselRefs,
  messagesContainerRef,
  isDarkMode,
  setResponding,
  isChatEmpty, 
  initialLoading,
  setInput,
  selectedMultiChips,
  setSelectedMultiChips,
}) => {
  const handleSuggestionClick = (phrase: SuggestionPhrase) => {
    sendMessage(phrase.text, phrase.backendPayload);
    setResponding(true);
  };
  
  // Aggiunto per gestire la classe sul body
  useEffect(() => {
    if (isChatEmpty) {
      document.body.classList.add('chat-empty');
    } else {
      document.body.classList.remove('chat-empty');
    }

    return () => {
      // Funzione di pulizia: rimuove la classe quando il componente smonta
      document.body.classList.remove('chat-empty');
    };
  }, [isChatEmpty]);

  return (
    <div className="messages" ref={messagesContainerRef}>
      {isChatEmpty && !isResponding && !initialLoading && (
        <SuggestionButtons
          onPhraseClick={handleSuggestionClick}
          isDarkMode={isDarkMode}
          disabled={isResponding}
           // Qui il bottone non dovrebbe mai essere disabilitato se la chat è vuota
        />
      )}
      {messages.map((msg, i) => {
        // Check if next message has suggestion chips
        const nextMessage = messages[i + 1];
        const isBeforeChips = !!(nextMessage && nextMessage.suggestionChips);
        
        return (
          <MessageRow
            key={msg.id}
            message={msg}
            index={i}
            userAvatar={userAvatar}
            botAvatar={botAvatar}
            sendMessage={sendMessage}
            scrollCarousel={scrollCarousel}
            carouselRefs={carouselRefs}
            setResponding={setResponding}
            isResponding={isResponding}
            messages={messages} 
            isLastMessage={i === messages.length - 1} 
            isDarkMode={isDarkMode}
            isBeforeChips={isBeforeChips}
            setInput={setInput}
            selectedMultiChips={selectedMultiChips}
            setSelectedMultiChips={setSelectedMultiChips}
          />
        );
      })}
      {(isTyping) && (
        <div className="message-row bot-row">
          <img src={botAvatar} alt="Bot" className="avatar" />
          <div className="message">
            <div className="typing-indicator">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </div>
        </div>
      )}
      <div ref={bottomRef} />
    </div>
  );
});

export default MessagesList;
